import { type CSSProperties } from 'react';

import { type Theme } from '@mui/material/styles';

import { type MuiThemeCallbackOrValue } from '../types/mui/sx';
import { type QuantitativeSize } from '../types/sizes';

import { evaluateHtmlColor } from './colors';
import { quantitativeSizeToString } from './sizes';

export interface BorderParams<
  C extends MuiThemeCallbackOrValue<string> | string = MuiThemeCallbackOrValue<string>,
> {
  readonly color?: C;
  readonly style?: 'dashed' | 'solid';
  readonly thickness?: QuantitativeSize<'px'>;
}

export const DefaultBorderParams = {
  color: (theme: Pick<Theme, 'palette'>) => theme.palette.state.secondary.borderColor,
  style: 'solid',
  thickness: '1px',
} as const;

const evaluateParams = (theme: Theme, params: BorderParams) => {
  const defaultedColor = params.color ?? DefaultBorderParams.color;
  return {
    color:
      typeof defaultedColor === 'function'
        ? evaluateHtmlColor(theme, defaultedColor(theme), { strict: false })
        : evaluateHtmlColor(theme, defaultedColor, { strict: false }),
    style: params.style ?? DefaultBorderParams.style,
    thickness: quantitativeSizeToString(params.thickness ?? DefaultBorderParams.thickness, 'px'),
  };
};

/**
 * A utility method that constructs a valid value for the 'border' CSS property using the defined
 * parameters, {@link BorderParams}.
 */
export const border = (theme: Theme, params: BorderParams) => {
  const { color, style, thickness } = evaluateParams(theme, params);
  return `${thickness} ${style} ${color}`;
};

interface ExhaustiveBorderStyleParams<
  C extends MuiThemeCallbackOrValue<string> | string = MuiThemeCallbackOrValue<string>,
> extends BorderParams<C> {
  /**
   * Defines whether or not the constructed CSS properties should be used to define the style
   * of a CSS outline or a CSS border.  If 'true', an outline will be used; otherwise, a border
   * will be used.
   *
   * @default false
   */
  readonly useOutline?: boolean;
}

export type ExhaustiveBorderStyleProperty =
  | 'border'
  | 'borderColor'
  | 'borderStyle'
  | 'borderWidth'
  | 'outline'
  | 'outlineColor'
  | 'outlineOffset'
  | 'outlineWidth';

export type ExhaustiveBorderStyleProperties = Pick<CSSProperties, ExhaustiveBorderStyleProperty>;

/**
 * A utility that constructs all of the relevant {@link CSSProperties} related to the
 * definition of a border or an outline, depending on the value of the parameter 'useOutline'.
 *
 * This can be used to ensure that there are no conflicts with MUI defined border or outline
 * properties and that all characteristics of an element's border or outline are overridden
 * and defined.
 */
export const exhaustiveBorderStyle = (
  theme: Theme,
  { useOutline = false, ...params }: ExhaustiveBorderStyleParams,
): CSSProperties => {
  const { color, style, thickness } = evaluateParams(theme, params);
  if (useOutline) {
    return {
      outline: border(theme, params),
      outlineColor: evaluateHtmlColor(theme, color),
      outlineOffset: `-${thickness}`,
      outlineWidth: thickness,
    };
  }
  return {
    border: border(theme, params),
    borderColor: color,
    borderStyle: style,
    borderWidth: thickness,
  };
};
